import { InformationCircleIcon } from '@heroicons/react/20/solid';
import React, { useMemo } from 'react';

import {
  DiscountBanner,
  ExpectedTag,
  FileAttachment
} from 'src/shared/components';
import { CustomFile } from 'src/shared/types/update';
import { formatMoney } from 'src/shared/utils/formatMoney';
import getDiscountLabel, {
  SampleTotalCostDiscount
} from 'src/shared/utils/getDiscountLabel';
import getPaymentTermsValue from 'src/shared/utils/getPaymentTermsValue';
import { pricingUnitPriceSuffix } from 'src/shared/utils/pricingUnit';

import ImageGallery from 'src/shared/components/ImageGallery';
import { cn } from 'src/shared/components/ui';
import { Tag, Typography } from 'src/shared/components/ui';

import isFileImage from '../../utils/isFileImage';
import { getCountryName } from '../quotes-tab/CountryFlag';
import MarkdownContent from '../updates-tab/markdown-content/MarkdownContent';

interface QuoteDetailTabContentProps {
  selectedQuote: any;
}

interface LabelValueProps {
  label: string;
  value: React.ReactNode | string;
  largeValue?: boolean;
  discount?: SampleTotalCostDiscount;
  estimated?: boolean;
}

export const LabelValue: React.FC<LabelValueProps> = ({
  label,
  value,
  largeValue = false,
  discount,
  estimated = false
}) => {
  const discountText = getDiscountLabel(discount);

  if (!value && !estimated) {
    return null;
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-1">
        <Typography
          variant="body-1"
          weight="medium"
          className="text-secondary-text"
        >
          {label}
        </Typography>
        {estimated && (
          <ExpectedTag popOverText="Final cost may vary slightly based on product weight, shipping rates, and applicable tariffs at the time of shipping." />
        )}
      </div>
      {discount && (
        <div className="flex items-center gap-2">
          <Typography
            variant={largeValue ? 'h5' : 'body-1'}
            weight="medium"
            className="line-through"
          >
            {value}
          </Typography>
          <Typography
            variant={largeValue ? 'h5' : 'body-1'}
            weight="medium"
            className="text-p-cavela-blue"
          >
            ${discount.discounted_price}
          </Typography>
          <Tag className="text-p-cavela-blue bg-tertiary">{discountText}</Tag>
        </div>
      )}
      {!discount && (
        <Typography variant={largeValue ? 'h5' : 'body-1'} weight="medium">
          {value}
        </Typography>
      )}
    </div>
  );
};

const getInfoBar = (text: string) => {
  return (
    <div className="flex items-start gap-1 bg-gray-100 border-gray-200 rounded-lg px-2 py-1.5">
      <div className="flex justify-start gap-2.5">
        <InformationCircleIcon className="w-4 h-4 relative top-0.5 text-primary" />
      </div>
      <Typography variant="body-2" weight="regular">
        {text}
      </Typography>
    </div>
  );
};

export const TariffValue: React.FC<any> = ({ selectedQuote }) => {
  if (!selectedQuote?.tariff) {
    if (selectedQuote?.ddp_unit_price_marked_up) {
      return `$${selectedQuote?.ddp_unit_price_marked_up}${pricingUnitPriceSuffix(
        selectedQuote?.pricing_unit
      )} (with tariff today, DDP)`;
    } else {
      return `-`;
    }
  } else {
    return (
      <>
        ${selectedQuote?.ddp_unit_price_marked_up}
        {pricingUnitPriceSuffix(selectedQuote?.pricing_unit)} (with tariff
        today, DDP)
        {getInfoBar(
          `The current tariff for imports of this kind from ${getCountryName(
            selectedQuote?.supplier_factory_country
          )} is ${selectedQuote?.tariff.duty_rate_percentage}%.`
        )}
      </>
    );
  }
};

const QuoteDetailTabContent: React.FC<QuoteDetailTabContentProps> = ({
  selectedQuote
}) => {
  const files = useMemo(() => {
    return selectedQuote.files.filter((file: CustomFile) => !isFileImage(file));
  }, [selectedQuote.files]);

  const renderFiles = () => {
    return files.map((file: CustomFile) => (
      <FileAttachment key={file.id} fileName={file.name} fileUrl={file.url} />
    ));
  };

  const images = useMemo(() => {
    return selectedQuote.files.filter((file: CustomFile) => isFileImage(file));
  }, [selectedQuote.files]);

  const withAdditionalFiles = useMemo(() => {
    return images.length > 0 || files.length > 0;
  }, [images, files]);

  const similarProductsFiles = useMemo(() => {
    return selectedQuote.similar_products.filter(
      (file: CustomFile) => !isFileImage(file)
    );
  }, [selectedQuote.similar_products]);

  const renderSimilarProductsFiles = () => {
    return similarProductsFiles.map((file: CustomFile) => (
      <FileAttachment key={file.id} fileName={file.name} fileUrl={file.url} />
    ));
  };

  const similarProductsImages = useMemo(() => {
    return selectedQuote.similar_products.filter((file: CustomFile) =>
      isFileImage(file)
    );
  }, [selectedQuote.similar_products]);

  const withSimilarProductsFiles = useMemo(() => {
    return similarProductsImages.length > 0 || similarProductsFiles.length > 0;
  }, [similarProductsImages, similarProductsFiles]);

  return (
    <div className="flex flex-col gap-5 pb-24">
      {withSimilarProductsFiles && (
        <div className="border-b border-gray-4 pb-7">
          <div className="px-5 flex flex-col gap-5">
            <Typography variant="h4" weight="bold">
              Similar products from this supplier
            </Typography>

            <div className="flex flex-col gap-2 mb-2">
              {similarProductsImages.length > 0 && (
                <ImageGallery isLarge imageUrls={similarProductsImages} />
              )}
              {similarProductsFiles.length > 0 && renderSimilarProductsFiles()}
            </div>
          </div>
        </div>
      )}

      {withAdditionalFiles && (
        <div className="border-b border-gray-4 pb-7">
          <div className="px-5 flex flex-col gap-5">
            <Typography variant="h4" weight="bold">
              Additional files
            </Typography>

            <div className="flex gap-2 items-center mb-2">
              {images.length > 0 && <ImageGallery imageUrls={images} />}
              {files.length > 0 && renderFiles()}
            </div>
          </div>
        </div>
      )}

      <div className="pb-7 border-b border-gray-4">
        <div className="px-5 flex flex-col gap-5">
          <Typography variant="h4" weight="bold">
            Product
          </Typography>

          <div className="grid grid-cols-2 gap-5">
            <LabelValue
              label="Production unit cost"
              value={
                selectedQuote?.exw_unit_price_marked_up
                  ? `${formatMoney(
                      selectedQuote?.exw_unit_price_marked_up
                    )}${pricingUnitPriceSuffix(selectedQuote?.pricing_unit)}`
                  : null
              }
            />
            <div className="flex flex-col">
              <LabelValue label="Shipped unit cost" value="" estimated={true} />
              <TariffValue selectedQuote={selectedQuote} />
            </div>
            <LabelValue
              label="Production time"
              value={
                selectedQuote?.production_time_days
                  ? `${selectedQuote?.production_time_days} days`
                  : null
              }
            />
            <LabelValue
              label="Payment terms"
              value={
                selectedQuote?.deposit_percentage
                  ? getPaymentTermsValue(selectedQuote.deposit_percentage)
                  : null
              }
            />

            <LabelValue
              label="Shipping method"
              value={selectedQuote?.bulk_shipping_option ?? null}
            />
            <LabelValue
              label="Shipping time"
              value={
                selectedQuote?.shipping_time_days
                  ? `${selectedQuote?.shipping_time_days} days`
                  : null
              }
            />
          </div>

          {(selectedQuote?.client_notes || selectedQuote?.quote_notes) && (
            <div className="flex flex-col">
              <Typography
                variant="body-1"
                weight="medium"
                className="text-secondary-text"
              >
                Notes
              </Typography>
              {selectedQuote?.client_notes && (
                <MarkdownContent markdownString={selectedQuote.client_notes} />
              )}

              {selectedQuote?.quote_notes && (
                <div className={cn(selectedQuote?.client_notes && 'mt-2')}>
                  <MarkdownContent markdownString={selectedQuote.quote_notes} />
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-b border-gray-4 pb-7">
        <div className="px-5 flex flex-col gap-5">
          <Typography variant="h4" weight="bold">
            Sample
          </Typography>

          <div className="grid grid-cols-2 gap-5">
            <LabelValue
              label="Production cost"
              value={
                selectedQuote?.sample_cost_marked_up
                  ? `$${selectedQuote?.sample_cost_marked_up}`
                  : null
              }
            />

            <LabelValue
              label="Production time"
              value={
                selectedQuote?.sample_production_time_days
                  ? `${selectedQuote?.sample_production_time_days} days`
                  : null
              }
            />
          </div>

          <div className="grid grid-cols-2 gap-5">
            <LabelValue label="Shipping method" value="Air Freight" />
            <LabelValue
              label="Shipping time"
              value={
                selectedQuote?.sample_shipping_time_days
                  ? `${selectedQuote?.sample_shipping_time_days} days`
                  : null
              }
            />
            <LabelValue
              label="Shipping cost"
              value={
                selectedQuote?.sample_shipping_cost_marked_up
                  ? `$${selectedQuote?.sample_shipping_cost_marked_up}`
                  : null
              }
            />
          </div>

          <div>
            <div className="flex flex-col gap-5">
              <LabelValue
                label="Total sample cost"
                value={
                  selectedQuote?.sample_total_cost_marked_up
                    ? `$${selectedQuote?.sample_total_cost_marked_up}`
                    : null
                }
                largeValue
                discount={selectedQuote?.sample_total_cost_discount}
              />
            </div>
            {selectedQuote?.sample_total_cost_discount && (
              <DiscountBanner localOrGlobal="local" />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteDetailTabContent;
